import React from "react";
import { RouteComponentProps, Route } from "react-router";
import { connect, ConnectedProps } from "react-redux";

import {
  Button,
  Grid,
  Dialog,
  TextField,
  makeStyles,
  Card,
  CardContent,
} from "@material-ui/core";
import IF from "../../common/IF";
import { array, object, string } from "yup";

import { ApplicationState } from "../../../store";
import * as GalleryStore from "../../../store/Gallery";
import usePrepareLink from "../../../hooks/router/usePrepareLink";
import { Formik } from "formik";
import { MultipleFileUploadField } from "../upload/MultipleFileUploadField";
import ImageBrowser, { BrowserMode } from "../browse/ImageBrowser";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiFormControl-root": {
      width: "60%",
      margin: theme.spacing(1),
    },
    "& .MuiButtonBase-root": {
      margin: theme.spacing(1, 0.5),
    },
  },
}));

export enum GalleryEditPurpose {
  Album = 0,
  Image = 1,
}

interface GalleryEditProps extends PropsFromRedux {
  purpose: GalleryEditPurpose;
}

const GalleryEdit: React.FC<
  GalleryEditProps & RouteComponentProps<{ albumId: string; imageId: string }>
> = ({
  match,
  purpose,
  history,
  updateGalleryElement,
  getGalleryElement,
  editGalleryElement,
  resetEditGalleryElement,
}) => {
  const classes = useStyles();

  const getGalleryElementId = React.useCallback(
    (editPurpose: GalleryEditPurpose) => {
      if (editPurpose === GalleryEditPurpose.Album) {
        return parseInt(match.params.albumId, 10) || 0;
      } else if (editPurpose === GalleryEditPurpose.Image) {
        return parseInt(match.params.imageId, 10) || 0;
      } else {
        return 0;
      }
    },
    []
  );

  const [gallery, setGallery] = React.useState<GalleryStore.GalleryElement>({
    elementName: "",
    imageUrl: "",
    elementClassId: 3,
    id: getGalleryElementId(purpose),
    parentId: 0,
    link: "",
    number: 0,
  });

  /// TODO: no need to release with first version
  const disableYoutubeSupport = true;

  const youtubeVideoEmbedLink = React.useMemo(() => {
    /*
            Example https://www.youtube.com/watch?v=M8EYygAwjU4
            must be presented as
            https://www.youtube.com/embed/M8EYygAwjU4?enablejsapi=1
        */

    let link = "";
    let match = (gallery.link ?? "").match(
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/
    );
    if (match && match[2].length === 11) {
      link = `https://www.youtube.com/embed/${match[2]}?enablejsapi=1`;
    }
    return link;
  }, [gallery.link]);

  const onSubmit = React.useCallback(
    async (event: React.ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (purpose === GalleryEditPurpose.Album) {
        await updateGalleryElement(gallery);
        history.push(`/admin/albums`);
        /// TODO: add popup message about action result
      } else if (purpose === GalleryEditPurpose.Image) {
        await updateGalleryElement(gallery);
        /// TODO: add popup message about action result
        history.push(
          `/admin/albums/${parseInt(match.params.albumId, 10) || 0}/images`
        );
      }
    },
    [purpose, gallery, updateGalleryElement]
  );

  React.useEffect(() => {
    const elementId = getGalleryElementId(purpose);
    if (editGalleryElement) {
      return;
    }

    getGalleryElement(elementId);
  }, [purpose, getGalleryElementId, editGalleryElement, getGalleryElement]);

  React.useEffect(() => {
    return () => {
      resetEditGalleryElement();
    };
  }, [resetEditGalleryElement]);

  React.useEffect(() => {
    if (!editGalleryElement) {
      return;
    }

    setGallery({
      ...gallery,
      elementName: editGalleryElement.elementName,
      imageUrl: editGalleryElement.imageUrl,
      number: editGalleryElement.number ?? 0,
      parentId: editGalleryElement.parentId,
    });
  }, [editGalleryElement]);

  const popupBrowse = usePrepareLink({
    to:
      purpose === GalleryEditPurpose.Album
        ? "/admin/albums/:albumId/edit/browse"
        : "/admin/albums/:albumId/images/:imageId/edit/browse",
    isRelativePath: false,
    hash: null,
  });

  const popupUpload = usePrepareLink({
    to:
      purpose === GalleryEditPurpose.Album
        ? "/admin/albums/:albumId/edit/upload"
        : "/admin/albums/:albumId/images/:imageId/edit/upload",
    isRelativePath: false,
    hash: null,
  });

  return (
    <>
      <Route
        exact
        path={[
          "/admin/albums/:albumId/edit/upload",
          "/admin/albums/:albumId/images/:imageId/edit/upload",
        ]}
        children={({ match }) => (
          <Dialog
            maxWidth={false}
            onClose={history.goBack}
            open={Boolean(match)}
            scroll="body"
          >
            <Card style={{ width: 600 }}>
              <CardContent>
                <Formik
                  initialValues={{ files: [] }}
                  validationSchema={object({
                    files: array(
                      object({
                        url: string().required(),
                      })
                    ),
                  })}
                  onSubmit={async (values) => {
                    console.log("values", values);
                    await new Promise((res) => setTimeout(res, 500));
                  }}
                >
                  {({ values, errors, isValid, isSubmitting }) => (
                    <div>
                      <Grid container spacing={2} direction="column">
                        <MultipleFileUploadField name="files" />
                      </Grid>
                    </div>
                  )}
                </Formik>
              </CardContent>
            </Card>
          </Dialog>
        )}
      />
      <Route
        exact
        path={[
          "/admin/albums/:albumId/edit/browse",
          "/admin/albums/:albumId/images/:imageId/edit/browse",
        ]}
        children={({ match }) => (
          <Dialog
            maxWidth={false}
            onClose={history.goBack}
            open={Boolean(match)}
            scroll="body"
            disableBackdropClick={true}
          >
            <ImageBrowser
              browserMode={BrowserMode.SingleMode}
              OnSelectFinished={(selectedItems) => {
                history.goBack();
                setGallery({
                  ...gallery,
                  imageUrl: selectedItems[0],
                });
              }}
              OnClose={history.goBack}
            />
          </Dialog>
        )}
      />
      <Button
        variant="contained"
        size="small"
        color="primary"
        onClick={() => {
          if (purpose === GalleryEditPurpose.Album) {
            history.push(`/admin/albums`);
          } else {
            history.push(
              `/admin/albums/${parseInt(match.params.albumId, 10) || 0}/images`
            );
          }
        }}
      >
        {purpose === GalleryEditPurpose.Album
          ? "Go to album list"
          : "Go to album"}
      </Button>
      <br />
      <br />
      <form className={classes.root} onSubmit={onSubmit}>
        <Grid container direction="column">
          <TextField
            variant="outlined"
            size="small"
            required
            value={gallery.elementName}
            label="Display"
            onChange={(e) => {
              setGallery({
                ...gallery,
                elementName: e.target.value,
              });
            }}
          />
          <Grid container>
            <TextField
              disabled
              variant="outlined"
              required
              value={gallery.imageUrl}
              label="ImageUrl"
              size="small"
              onChange={(e) => {
                setGallery({
                  ...gallery,
                  imageUrl: e.target.value,
                });
              }}
            />
            <Button
              variant="contained"
              size="small"
              color="default"
              onClick={() => {
                history.push(
                  popupBrowse.pathname
                    .replace(":albumId", match.params.albumId)
                    .replace(":imageId", match.params.imageId)
                );
              }}
            >
              Browse
            </Button>
            <Button
              variant="contained"
              size="small"
              color="default"
              onClick={() => {
                history.push(
                  popupUpload.pathname
                    .replace(":albumId", match.params.albumId)
                    .replace(":imageId", match.params.imageId)
                );
              }}
            >
              Upload
            </Button>
          </Grid>
          <IF
            condition={
              !disableYoutubeSupport && purpose === GalleryEditPurpose.Image
            }
          >
            <TextField
              variant="outlined"
              label="Youtube Link"
              value={gallery.link}
              size="small"
              onChange={(e) => {
                setGallery({
                  ...gallery,
                  link: e.target.value,
                });
              }}
            />
            <TextField
              disabled
              variant="outlined"
              value={youtubeVideoEmbedLink}
              label="Youtube Link Embed"
              size="small"
            />
          </IF>
          <TextField
            variant="outlined"
            value={gallery.number}
            name="numberFormat"
            label="Number"
            size="small"
            onChange={(e) => {
              const target = Number(e.target.value);
              if (!isNaN(target) && target >= 0 && target < 10000) {
                setGallery({
                  ...gallery,
                  number: target,
                });
              }
            }}
            type="numberFormat"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
          <Button
            variant="contained"
            size="large"
            type="submit"
            color="primary"
            style={{ width: 100 }}
          >
            Save
          </Button>
        </Grid>
      </form>
    </>
  );
};

const mapStateToProps = (state: ApplicationState) => ({
  galleryElements: state.gallery?.galleryElementsByParentId,
  editGalleryElement: state.gallery?.editGalleryElement,
});

const mapDispatch = {
  updateGalleryElement: (element: GalleryStore.GalleryElement) =>
    GalleryStore.actionCreators.updateGalleryElement(element),
  getGalleryElement: (elementId: number) =>
    GalleryStore.actionCreators.getGalleryElementForEdit(elementId),
  resetEditGalleryElement: GalleryStore.actionCreators.resetEditGalleryElement,
};

const connector = connect(mapStateToProps, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(GalleryEdit);
